<template>
  <div class="container mt-5">
    <h4 class="mb-4" style="color: #b03a45;">
      <i class="fas fa-briefcase me-2 text-warning"></i>{{ $t('professionalEdit.title') }}
    </h4>

    <!-- Loader -->
    <div v-if="!ready" class="text-center py-5 text-muted">{{ $t('professionalEdit.loading') }}</div>

    <!-- Form -->
    <form v-else @submit.prevent="handleSubmit">
      <div class="row">
        <!-- Company -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.company') }}</label>
            <select v-model="form.company_id" class="form-select">
              <option disabled value="">{{ $t('professionalEdit.select') }}</option>
              <option v-for="c in companies" :key="c.id" :value="c.id">
                {{ c.english_name }}
              </option>
            </select>
            <small v-if="validationErrors.company_id" class="text-danger">
              {{ validationErrors.company_id[0] }}
            </small>
          </div>
        </div>

        <!-- Project -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.project') }}</label>
            <select v-model="form.project_id" class="form-select">
              <option disabled value="">{{ $t('professionalEdit.select') }}</option>
              <option v-for="p in projects" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
            <small v-if="validationErrors.project_id" class="text-danger">
              {{ validationErrors.project_id[0] }}
            </small>
          </div>
        </div>

        <!-- Position -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.position') }}</label>
            <select v-model="form.position_id" class="form-select">
              <option disabled value="">{{ $t('professionalEdit.select') }}</option>
              <option v-for="pos in positions" :key="pos.id" :value="pos.id">
                {{ pos.english_name }}
              </option>
            </select>
            <small v-if="validationErrors.position_id" class="text-danger">
              {{ validationErrors.position_id[0] }}
            </small>
          </div>
        </div>

        <!-- Status -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.status') }}</label>
            <select v-model="form.status" class="form-select" required>
              <option v-for="s in statusOptions" :key="s.value" :value="s.value">
                {{ s.label }}
              </option>
            </select>
            <small v-if="validationErrors.status" class="text-danger">
              {{ validationErrors.status[0] }}
            </small>
          </div>
        </div>

        <!-- Manager -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.manager') }}</label>
            <select v-model="form.manager_id" class="form-select">
              <option disabled value="">{{ $t('professionalEdit.select') }}</option>
              <option v-for="m in managers" :key="m.id" :value="m.id">
                {{ m.firstname }} {{ m.lastname }}
              </option>
            </select>
          </div>
        </div>

        <!-- Supervisor -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.supervisor') }}</label>
            <select v-model="form.supervision_id" class="form-select">
              <option disabled value="">{{ $t('professionalEdit.select') }}</option>
              <option v-for="s in supervisors" :key="s.id" :value="s.id">
                {{ s.firstname }} {{ s.lastname }}
              </option>
            </select>
          </div>
        </div>

        <!-- Hire Date -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.hireDate') }}</label>
            <input type="date" v-model="form.hire_date" class="form-control" />
          </div>
        </div>

        <!-- Work Email -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.workEmail') }}</label>
            <input type="email" v-model="form.work_email" class="form-control" placeholder="employee@company.com" />
          </div>
        </div>

        <!-- Work Number -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.workNumber') }}</label>
            <input type="text" v-model="form.work_number" class="form-control" placeholder="Work number" />
          </div>
        </div>

        <!-- CIN -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.cin') }}</label>
            <input type="text" v-model="form.cin" class="form-control" placeholder="National ID" />
          </div>
        </div>

        <!-- CIN Release Date -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.cinReleaseDate') }}</label>
            <input type="date" v-model="form.cin_release_date" class="form-control" required />
          </div>
        </div>

        <!-- CNSS Number -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.cnss') }}</label>
            <input type="text" v-model="form.cnss_number" class="form-control" />
          </div>
        </div>

        <!-- Diploma Type -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.diploma') }}</label>
            <input type="text" v-model="form.diploma_type" class="form-control" placeholder="Master, Bachelor..." />
          </div>
        </div>

        <!-- Employee Code -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.employeeCode') }}</label>
            <input type="text" v-model="form.code" class="form-control" />
          </div>
        </div>

        <!-- Description -->
        <div class="col-12 mb-2">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">{{ $t('professionalEdit.fields.description') }}</label>
            <textarea v-model="form.description" class="form-control" rows="3" placeholder="Additional info"></textarea>
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="d-flex justify-content-end gap-3 mt-3">
        <button type="button" class="btn action-btn" @click="goBack" :title="$t('professionalEdit.buttons.back')">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button type="submit" class="btn action-btn" :title="$t('professionalEdit.buttons.save')">
          <i class="fas fa-save"></i>
        </button>
        <button type="button" class="btn action-btn" @click="goNext" :title="$t('professionalEdit.buttons.next')">
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>
    </form>
  </div>
</template>


<script setup>
import { reactive, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'

const route = useRoute()
const router = useRouter()
const toast = useToast()

// ✅ Back vers la page Personal qui marche chez toi
const BACK_ROUTE = '/employee/information/edit'

const userId = String(route.query.user_id || route.params.user_id || '')

const infoId = ref(null)
const ready = ref(false)
const isLoading = ref(false)

const form = reactive({
  user_id: userId,
  code: '',
  company_id: '',
  project_id: '',
  position_id: '',
  manager_id: '',
  supervision_id: '',
  hire_date: '',
  status: 'Active',
  work_email: '',          // affiché mais pas envoyé
  work_number: '',
  cin: '',
  cin_release_date: '',
  cnss_number: '',
  diploma_type: '',
  description: '',
})

const validationErrors = ref({})

const companies = ref([])
const projects  = ref([])
const positions = ref([])
const managers  = ref([])
const supervisors = ref([])

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'On Leave', label: 'On Leave' },
  { value: 'Resigned', label: 'Resigned' },
  { value: 'Terminated', label: 'Terminated' },
]

const unwrap = (res) => (res?.data?.data ?? res?.data ?? [])

// dropdowns
const fetchDropdownData = async () => {
  try {
    const [c, p, pos, m, s] = await Promise.all([
      axios.get('/companies'),
      axios.get('/projects'),
      axios.get('/positions'),
      axios.get('/employee/leaves/managers'),
      axios.get('/employee/leaves/supervisors'),
    ])
    companies.value   = unwrap(c)
    projects.value    = unwrap(p)
    positions.value   = unwrap(pos)
    managers.value    = m?.data?.managers ?? []
    supervisors.value = s?.data?.supervisors ?? []
  } catch (err) {
    toast.error('Failed to load reference data')
  }
}

const fetchProfessionalInfo = async () => {
  if (!userId) return
  try {
    isLoading.value = true
    const res = await axios.get(`/employee/professional-information/${userId}`)
    const data = res?.data?.data
    if (!data) return
    infoId.value = data.id
    Object.assign(form, {
      user_id: userId,
      code: data.code ?? '',
      company_id: data.company?.id ?? '',
      project_id: data.project?.id ?? '',
      position_id: data.position?.id ?? '',
      manager_id: data.manager_id ?? '',
      supervision_id: data.supervision_id ?? '',
      hire_date: data.hire_date ?? '',
      status: data.status ?? 'Active',
      work_email: data.work_email ?? '',
      work_number: data.work_number ?? '',
      cin: data.cin ?? '',
      cin_release_date: data.cin_release_date ?? '',
      cnss_number: data.cnss_number ?? '',
      diploma_type: data.diploma_type ?? '',
      description: data.description ?? '',
    })
  } catch (err) {
    if (err.response?.status === 404) {
      toast.info('No professional information found. You can create one.')
    } else {
      toast.error('Failed to load professional information')
    }
  } finally {
    isLoading.value = false
  }
}

// helpers
const toId = (v) => (v === '' || v === undefined || v === null ? null : Number(v))
const toDate = (v) => {
  if (!v) return null
  const d = new Date(v)
  if (isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

const handleSubmit = async () => {
  validationErrors.value = {}

  if (!form.status) {
    validationErrors.value.status = ['Status is required']
    toast.error('Status is required')
    return
  }
  if (!form.cin_release_date) {
    validationErrors.value.cin_release_date = ['CIN release date is required']
    toast.error('CIN release date is required')
    return
  }

  try {
    isLoading.value = true

    // ⚠️ ne pas envoyer work_email (colonne absente)
    const payload = {
      user_id: Number(userId),
      code: form.code || null,
      cin: form.cin || null,
      company_id: toId(form.company_id),
      project_id: toId(form.project_id),
      position_id: toId(form.position_id),
      manager_id: toId(form.manager_id),
      supervision_id: toId(form.supervision_id),
      hire_date: toDate(form.hire_date),
      status: form.status,
      work_number: form.work_number || null,
      diploma_type: form.diploma_type || null,
      description: form.description || null,
      cin_release_date: toDate(form.cin_release_date),
      cnss_number: form.cnss_number || null,
    }

    const res = infoId.value
      ? await axios.put(`/employee/professional-information/${infoId.value}`, payload)
      : await axios.post('/employee/professional-information', payload)

    if (!infoId.value && res?.data?.data?.id) infoId.value = res.data.data.id
    toast.success('Professional information saved successfully')
  } catch (err) {
    if (err.response?.status === 422) {
      validationErrors.value = err.response.data.errors || {}
      const msg = Object.values(validationErrors.value).flat().join(', ')
      toast.error(msg || 'Validation error')
    } else {
      const msg = err.response?.data?.message || 'Failed to save professional information'
      toast.error(msg)
    }
  } finally {
    isLoading.value = false
  }
}

const goBack = () => {
  router.push({ path: BACK_ROUTE, query: { user_id: userId } })
}
const goNext = () => {
  router.push({
    path: '/employee/bank-details/edit',
    query: { user_id: userId }
  })
}


onMounted(async () => {
  try {
    await fetchDropdownData()
    await fetchProfessionalInfo()
  } finally {
    ready.value = true
  }
})
</script>

<style scoped>
.action-btn{
  background-color:#b03a45;
  color:#fff;
  width:50px; height:45px;
  border-radius:12px;
  transition:transform .15s ease, box-shadow .15s ease;
}
.action-btn:hover{
  transform:translateY(-1px);
  box-shadow:0 6px 14px rgba(176,58,69,.25);
}
</style>
